import React, { useState, useEffect, useMemo } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import CartDrawer from '../components/cart/CartDrawer';
import ProductCard from '../components/products/ProductCard';
import { products, categories, siteImages } from '../data/mock';
import { useCart } from '../context/CartContext';
import { Filter, X, ChevronDown, Grid3X3, List, Check, SlidersHorizontal, ShoppingBag } from 'lucide-react';

const ShopPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [showFilters, setShowFilters] = useState(false);
  const [sortBy, setSortBy] = useState('featured');
  const [viewMode, setViewMode] = useState('grid');
  const { addToCart } = useCart();

  const selectedCategory = searchParams.get('category') || '';
  const activeCategory = categories.find(c => c.slug === selectedCategory);

  useEffect(() => {
    document.title = 'Sports Equipment & Training Accessories | Sport Fitt Pro';
  }, []);

  const filteredProducts = useMemo(() => {
    let result = [...products];
    if (selectedCategory) {
      result = result.filter(p => p.categorySlug === selectedCategory);
    }
    switch (sortBy) {
      case 'price-low': result.sort((a, b) => a.price - b.price); break;
      case 'price-high': result.sort((a, b) => b.price - a.price); break;
      case 'name': result.sort((a, b) => a.name.localeCompare(b.name)); break;
      default: break;
    }
    return result;
  }, [selectedCategory, sortBy]);

  const handleCategoryChange = (categorySlug) => {
    if (categorySlug) setSearchParams({ category: categorySlug });
    else setSearchParams({});
  };

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <CartDrawer />

      <main className="pt-[104px]">
        {/* Banner */}
        <section className="relative h-72 md:h-96 overflow-hidden bg-ink-950">
          <img
            src={siteImages.categoryBanner || siteImages.shopBanner}
            alt="Sports Equipment"
            className="absolute inset-0 w-full h-full object-cover opacity-50"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-ink-950 via-ink-950/80 to-transparent" />
          <div className="absolute bottom-0 right-0 w-1/2 h-2 bg-athletic-500" />

          <div className="relative h-full max-w-7xl mx-auto px-4 lg:px-8 flex items-end pb-12">
            <div>
              <nav className="flex items-center gap-2 text-white/60 text-xs font-bold uppercase tracking-wider mb-4">
                <Link to="/" className="hover:text-athletic-400 transition-colors">Home</Link>
                <span>/</span>
                <span className="text-athletic-400">Shop</span>
                {activeCategory && (
                  <>
                    <span>/</span>
                    <span className="text-white">{activeCategory.name}</span>
                  </>
                )}
              </nav>
              <p className="text-athletic-400 text-[11px] font-black tracking-[0.4em] uppercase mb-3">
                {activeCategory ? 'Category' : 'All Collections'}
              </p>
              <h1 className="font-display text-5xl md:text-7xl text-white leading-none tracking-wider">
                {activeCategory ? activeCategory.name.toUpperCase() : 'SHOP ALL GEAR'}
              </h1>
              <p className="text-white/70 mt-4 max-w-lg text-sm">
                {activeCategory ? activeCategory.description : 'Premium sports gear and fitness equipment engineered for performance.'}
              </p>
            </div>
          </div>
        </section>

        <div className="max-w-7xl mx-auto px-4 lg:px-8 py-12">
          <div className="flex flex-col lg:flex-row gap-10">

            {/* Sidebar */}
            <aside className={`lg:w-64 flex-shrink-0 ${showFilters ? 'block fixed inset-0 z-40 bg-white p-6 overflow-y-auto lg:static lg:p-0' : 'hidden lg:block'}`}>
              <div className="lg:sticky lg:top-28">
                <div className="flex items-center justify-between mb-5">
                  <h2 className="font-display text-2xl text-ink-900 tracking-wider">CATEGORIES</h2>
                  <button onClick={() => setShowFilters(false)} className="lg:hidden text-ink-500 hover:text-ink-900">
                    <X className="w-5 h-5" />
                  </button>
                </div>

                <div className="border-t-2 border-ink-900">
                  <button
                    onClick={() => handleCategoryChange('')}
                    className={`w-full text-left px-0 py-3 text-sm font-bold uppercase tracking-wider border-b border-ink-100 flex items-center justify-between transition-colors ${
                      !selectedCategory ? 'text-athletic-500' : 'text-ink-800 hover:text-athletic-500'
                    }`}
                  >
                    <span>All Products</span>
                    <span className="text-xs text-ink-400">{products.length}</span>
                  </button>
                  {categories.map(cat => {
                    const count = products.filter(p => p.categorySlug === cat.slug).length;
                    const active = selectedCategory === cat.slug;
                    return (
                      <button
                        key={cat.id}
                        onClick={() => handleCategoryChange(cat.slug)}
                        className={`w-full text-left px-0 py-3 text-sm font-bold uppercase tracking-wider border-b border-ink-100 flex items-center justify-between transition-colors ${
                          active ? 'text-athletic-500' : 'text-ink-800 hover:text-athletic-500'
                        }`}
                      >
                        <span className="flex items-center gap-2">
                          {active && <span className="block w-2 h-2 bg-athletic-500 rounded-full" />}
                          {cat.name}
                        </span>
                        <span className="text-xs text-ink-400">{count}</span>
                      </button>
                    );
                  })}
                </div>

                {/* Promo Card */}
                <div className="mt-8 bg-ink-950 text-white p-6 border-l-4 border-athletic-500">
                  <p className="text-athletic-400 text-[10px] font-black tracking-[0.35em] uppercase mb-2">Free Shipping</p>
                  <p className="font-display text-3xl leading-none tracking-wider mb-3">ON $75+ ORDERS</p>
                  <p className="text-white/70 text-xs">USA Shipping. No Exceptions.</p>
                </div>
              </div>
            </aside>

            {/* Main Grid */}
            <div className="flex-1">
              {/* Toolbar */}
              <div className="flex flex-wrap items-center justify-between gap-4 pb-5 mb-8 border-b-2 border-ink-900">
                <div className="flex items-center gap-4">
                  <button
                    onClick={() => setShowFilters(true)}
                    className="lg:hidden inline-flex items-center gap-2 px-4 py-2 bg-ink-900 text-white font-bold uppercase text-xs tracking-wider"
                  >
                    <SlidersHorizontal className="w-4 h-4" />
                    Filters
                  </button>
                  <p className="text-ink-600 text-sm">
                    Showing <span className="font-black text-ink-900">{filteredProducts.length}</span> product{filteredProducts.length !== 1 ? 's' : ''}
                  </p>
                </div>

                <div className="flex items-center gap-3">
                  <div className="hidden sm:flex items-center border border-ink-200">
                    <button
                      onClick={() => setViewMode('grid')}
                      className={`p-2.5 transition-colors ${viewMode === 'grid' ? 'bg-ink-900 text-white' : 'text-ink-600 hover:text-ink-900'}`}
                    >
                      <Grid3X3 className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => setViewMode('list')}
                      className={`p-2.5 transition-colors ${viewMode === 'list' ? 'bg-ink-900 text-white' : 'text-ink-600 hover:text-ink-900'}`}
                    >
                      <List className="w-4 h-4" />
                    </button>
                  </div>

                  <div className="relative">
                    <select
                      value={sortBy}
                      onChange={(e) => setSortBy(e.target.value)}
                      className="appearance-none bg-white border border-ink-200 px-4 py-2.5 pr-10 text-sm text-ink-900 font-bold uppercase tracking-wider focus:outline-none focus:border-athletic-500 cursor-pointer"
                    >
                      <option value="featured">Featured</option>
                      <option value="price-low">Price: Low → High</option>
                      <option value="price-high">Price: High → Low</option>
                      <option value="name">Name: A–Z</option>
                    </select>
                    <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-ink-500 pointer-events-none" />
                  </div>
                </div>
              </div>

              {/* Products */}
              {filteredProducts.length === 0 ? (
                <div className="text-center py-20 bg-ink-50 border-2 border-dashed border-ink-200">
                  <ShoppingBag className="w-16 h-16 text-ink-300 mx-auto mb-4" />
                  <h3 className="font-display text-2xl text-ink-900 tracking-wider mb-2">NO PRODUCTS FOUND</h3>
                  <p className="text-ink-500 mb-6">Try adjusting your filters to see more</p>
                  <button
                    onClick={() => handleCategoryChange('')}
                    className="px-6 py-3 bg-athletic-500 text-white font-black uppercase tracking-wider text-sm hover:bg-athletic-600 transition-colors"
                  >
                    View All Products
                  </button>
                </div>
              ) : viewMode === 'grid' ? (
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
                  {filteredProducts.map(product => (
                    <ProductCard key={product.id} product={product} />
                  ))}
                </div>
              ) : (
                <div className="space-y-4">
                  {filteredProducts.map(product => {
                    const cat = categories.find(c => c.id === product.categoryId);
                    return (
                      <Link
                        key={product.id}
                        to={`/product/${product.slug}`}
                        className="group flex bg-white border-2 border-ink-100 hover:border-athletic-500 transition-all overflow-hidden"
                      >
                        <div className="w-40 sm:w-56 aspect-square flex-shrink-0 bg-ink-50 overflow-hidden">
                          <img
                            src={product.image}
                            alt={product.name}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                          />
                        </div>
                        <div className="flex-1 p-5 sm:p-6 flex flex-col justify-between">
                          <div>
                            <p className="text-[10px] text-ink-400 font-bold uppercase tracking-[0.25em] mb-1">{cat?.name}</p>
                            <h3 className="font-bold text-ink-900 text-lg mb-2 group-hover:text-athletic-500 transition-colors">
                              {product.name}
                            </h3>
                            <p className="text-ink-500 text-sm line-clamp-2 mb-4">{product.description}</p>
                          </div>
                          <div className="flex items-center justify-between">
                            <span className="text-2xl font-black text-ink-900">${product.price.toFixed(2)}</span>
                            <button
                              onClick={(e) => { e.preventDefault(); addToCart(product); }}
                              className="flex items-center gap-2 px-4 py-2 bg-ink-900 hover:bg-athletic-500 text-white font-black uppercase text-xs tracking-wider transition-colors"
                            >
                              <ShoppingBag className="w-3.5 h-3.5" />
                              Add to Cart
                            </button>
                          </div>
                        </div>
                      </Link>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default ShopPage;
